/**
 * Mock data for the Accounting Rules module.
 *
 * This file is the ONLY place that fakes server data for Rules.
 * When the backend is wired up, each MOCK_* export is replaced
 * by an API fetch inside useRules. No UI component changes.
 *
 * Figures below reflect a mid-usage account (7 bank rules, 3
 * integration rules). The "empty state" scene is triggered by
 * passing an empty array to the Rules page, not by changing this
 * file.
 */

import type {
  Rule,
  RuleConditionKey,
  RuleConditionOperator,
  BankBrand,
  IntegrationSource,
} from "./rulesTypes";

/** Bank rules (apply to transactions imported from bank feeds). */
export const MOCK_BANK_RULES: Rule[] = [
  {
    id: "rule_bank_001",
    tab: "bank",
    priority: 1,
    name: "Costco → Office supplies",
    appliedTo: {
      type: "specific_bank_account",
      bankAccountId: "acc_td_visa_0028",
      bankAccountName: "TD Visa",
      bankBrand: "td",
    },
    conditions: [
      { id: "c1", key: "payee", operator: "contains", value: "costco" },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Office supplies",
      reimbursable: true,
    },
    status: "active",
    autoPost: true,
    matchCount: 24,
    lastAppliedAt: "2026-10-20T22:05:00Z",
    createdAt: "2026-07-15T10:00:00Z",
    updatedAt: "2026-07-15T10:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_bank_002",
    tab: "bank",
    priority: 2,
    name: "Payroll biweekly → Wages expense",
    appliedTo: {
      type: "specific_bank_account",
      bankAccountId: "acc_rbc_chq_4821",
      bankAccountName: "RBC Chequing",
      bankBrand: "rbc",
    },
    conditions: [
      { id: "c1", key: "payee", operator: "contains", value: "payroll" },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Wages expense",
    },
    status: "active",
    autoPost: false,
    matchCount: 12,
    lastAppliedAt: "2026-10-18T14:30:00Z",
    createdAt: "2026-06-01T09:00:00Z",
    updatedAt: "2026-06-01T09:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_bank_003",
    tab: "bank",
    priority: 3,
    name: "Shaw → Utilities",
    appliedTo: {
      type: "specific_bank_account",
      bankAccountId: "acc_td_visa_0028",
      bankAccountName: "TD Visa",
      bankBrand: "td",
    },
    conditions: [
      { id: "c1", key: "payee", operator: "contains", value: "shaw" },
      { id: "c2", key: "amount", operator: "less_than", value: 500 },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Utilities",
      gstTreatment: "included",
    },
    status: "active",
    autoPost: true,
    matchCount: 6,
    lastAppliedAt: "2026-10-17T08:12:00Z",
    createdAt: "2026-05-20T11:00:00Z",
    updatedAt: "2026-09-10T14:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_bank_004",
    tab: "bank",
    priority: 4,
    name: "Interac e-Transfer in → Owner contribution",
    appliedTo: { type: "all_bank_accounts" },
    conditions: [
      { id: "c1", key: "payee", operator: "contains", value: "interac" },
      { id: "c2", key: "direction", operator: "is", value: "credit" },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Owner contribution",
    },
    status: "active",
    autoPost: false,
    matchCount: 3,
    lastAppliedAt: "2026-10-14T19:45:00Z",
    createdAt: "2026-08-10T15:00:00Z",
    updatedAt: "2026-08-10T15:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_bank_005",
    tab: "bank",
    priority: 5,
    name: "Uber → Travel, reimbursable",
    appliedTo: {
      type: "specific_bank_account",
      bankAccountId: "acc_td_visa_0028",
      bankAccountName: "TD Visa",
      bankBrand: "td",
    },
    conditions: [
      { id: "c1", key: "payee", operator: "contains", value: "uber" },
      { id: "c2", key: "amount", operator: "less_than", value: 100 },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Travel",
      billable: true,
    },
    status: "active",
    autoPost: true,
    matchCount: 9,
    lastAppliedAt: "2026-10-15T12:30:00Z",
    createdAt: "2026-07-01T10:00:00Z",
    updatedAt: "2026-07-01T10:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_bank_006",
    tab: "bank",
    priority: 6,
    name: "Amazon → Office supplies",
    appliedTo: { type: "all_bank_accounts" },
    conditions: [
      { id: "c1", key: "payee", operator: "contains", value: "amazon" },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Office supplies",
    },
    status: "paused",
    autoPost: false,
    matchCount: 42,
    lastAppliedAt: "2026-10-07T16:00:00Z",
    createdAt: "2026-04-15T09:00:00Z",
    updatedAt: "2026-10-07T16:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_bank_007",
    tab: "bank",
    priority: 7,
    name: "Rogers → Internet & phone",
    appliedTo: {
      type: "specific_bank_account",
      bankAccountId: "acc_rbc_chq_4821",
      bankAccountName: "RBC Chequing",
      bankBrand: "rbc",
    },
    conditions: [
      { id: "c1", key: "payee", operator: "contains", value: "rogers" },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Internet & phone",
    },
    status: "active",
    autoPost: true,
    matchCount: 11,
    lastAppliedAt: "2026-10-11T07:20:00Z",
    createdAt: "2026-06-20T10:00:00Z",
    updatedAt: "2026-06-20T10:00:00Z",
    createdBy: "user_claire",
  },
];

/** Integration rules (apply to transactions from Stripe, Shopify, etc.). */
export const MOCK_INTEGRATION_RULES: Rule[] = [
  {
    id: "rule_int_001",
    tab: "integration",
    priority: 1,
    name: "Shopify orders → Online sales income",
    appliedTo: {
      type: "integration_source",
      integrationSource: "shopify",
      integrationName: "Shopify",
    },
    conditions: [
      { id: "c1", key: "orderStatus", operator: "is", value: "paid" },
      { id: "c2", key: "orderTotal", operator: "greater_than", value: 0 },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Online sales income",
      account: "RBC Chequing",
      gstTreatment: "included",
    },
    status: "active",
    autoPost: true,
    matchCount: 86,
    lastAppliedAt: "2026-10-20T23:45:00Z",
    createdAt: "2026-08-01T10:00:00Z",
    updatedAt: "2026-08-01T10:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_int_002",
    tab: "integration",
    priority: 2,
    name: "Stripe payouts → RBC Chequing deposits",
    appliedTo: {
      type: "integration_source",
      integrationSource: "stripe",
      integrationName: "Stripe",
    },
    conditions: [
      { id: "c1", key: "transactionType", operator: "is", value: "payout" },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Sales income",
      account: "RBC Chequing",
    },
    status: "active",
    autoPost: true,
    matchCount: 24,
    lastAppliedAt: "2026-10-19T18:00:00Z",
    createdAt: "2026-08-05T10:00:00Z",
    updatedAt: "2026-08-05T10:00:00Z",
    createdBy: "user_claire",
  },
  {
    id: "rule_int_003",
    tab: "integration",
    priority: 3,
    name: "Square tips → Tips payable",
    appliedTo: {
      type: "integration_source",
      integrationSource: "square",
      integrationName: "Square",
    },
    conditions: [
      { id: "c1", key: "lineItemType", operator: "is", value: "tip" },
    ],
    conditionJoin: "AND",
    actions: {
      category: "Tips payable",
      gstTreatment: "not_applicable",
    },
    status: "active",
    autoPost: true,
    matchCount: 18,
    lastAppliedAt: "2026-10-18T20:30:00Z",
    createdAt: "2026-09-10T10:00:00Z",
    updatedAt: "2026-09-10T10:00:00Z",
    createdBy: "user_claire",
  },
];

/** Human-readable labels for condition keys (used by RuleRow + Rule builder). */
export const CONDITION_KEY_LABELS: Record<RuleConditionKey, string> = {
  payee: "Payee",
  amount: "Amount",
  direction: "Direction",
  bankAccount: "Bank account",
  orderStatus: "Order status",
  orderTotal: "Order total",
  transactionType: "Transaction type",
  lineItemType: "Line item type",
  paymentMethod: "Payment method",
  productCategory: "Product category",
};

/** Human-readable labels for condition operators. */
export const OPERATOR_LABELS: Record<RuleConditionOperator, string> = {
  contains: "contains",
  equals: "equals",
  starts_with: "starts with",
  ends_with: "ends with",
  less_than: "less than",
  greater_than: "greater than",
  between: "between",
  is: "is",
};

/** Brand color overrides for the "Applied to" chip dot (bank brands). */
export const BANK_BRAND_COLORS: Record<BankBrand, string> = {
  rbc: "#FFCA00",
  td: "#008D4C",
  scotia: "#ED1C24",
  bmo: "#0075BE",
  cibc: "#8B1F3F",
  national_bank: "#E31837",
  tangerine: "#F57C00",
  simplii: "#E31E24",
  desjardins: "#00874E",
  other: "#6B7684",
};

/** Brand color overrides for the "Applied to" chip dot (integrations). */
export const INTEGRATION_SOURCE_COLORS: Record<IntegrationSource, string> = {
  stripe: "#635BFF",
  shopify: "#96BF48",
  square: "#000000",
  amazon: "#FF9900",
  paypal: "#003087",
  ebay: "#E53238",
  etsy: "#F56400",
};